import api from './api';

export interface PipelineStage {
  id: string;
  name: string;
  position: number;
  probability: number;
  isWon: boolean;
  isLost: boolean;
}

export interface DealLead {
  id: string;
  email: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  /** Nur im Detail (getDeal laedt den ganzen Lead). */
  lemlistCampaignId?: string | null;
}

export interface DealNote {
  id: string;
  dealId: string;
  text: string;
  kind: string;
  createdAt: string;
}

export interface Deal {
  id: string;
  title: string;
  company?: string;
  value: number;
  currency: string;
  stageId: string;
  status: string;
  lostReason?: string;
  closedAt?: string;
  expectedCloseAt?: string;
  nextStep?: string;
  nextStepAt?: string;
  source?: string;
  leadId?: string;
  /** Firmenakte, wenn der Vorgang aus einer Antwort entstand oder zugeordnet wurde. */
  companyId?: string | null;
  createdAt: string;
  updatedAt: string;
  stage?: PipelineStage;
  lead?: DealLead | null;
  notes?: DealNote[];
  /** Nur im Detail: was am Vorgang haengt. */
  tasks?: { id: string; title: string; status: string; dueAt?: string | null }[];
  quotes?: { id: string; number: string; status: string }[];
  orders?: { id: string; number?: string | null; status: string }[];
  invoices?: { id: string; number?: string | null; status: string; art: string }[];
}

export interface PipelineSummary {
  offeneVorgaenge: number;
  offenerWert: number;
  gewichteterWert: number;
  gewonnen: number;
  gewonnenerWert: number;
  verloren: number;
}

/* --- Dubletten (aus Intermal): Leads finden und zusammenfuehren --------- */

export interface DedupeLead {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  company?: string | null;
  jobTitle?: string | null;
  linkedInUrl?: string | null;
  phone?: string | null;
  website?: string | null;
  location?: string | null;
  notes?: string | null;
  updatedAt: string;
  createdAt: string;
}

export interface DuplicateCluster {
  score: number;
  reasons: string[];
  leadIds: string[];
  leads: DedupeLead[];
  suggestedPrimaryId: string;
  mergedPreview: Record<string, string | null>;
}

const crmService = {
  async getStages(): Promise<PipelineStage[]> {
    const response = await api.get('/crm/stages');
    return response.data;
  },

  async createStage(daten: { name: string; probability?: number }): Promise<PipelineStage> {
    const response = await api.post('/crm/stages', daten);
    return response.data;
  },

  async updateStage(id: string, daten: Partial<PipelineStage>): Promise<PipelineStage> {
    const response = await api.patch(`/crm/stages/${id}`, daten);
    return response.data;
  },

  async deleteStage(id: string): Promise<void> {
    await api.delete(`/crm/stages/${id}`);
  },

  async getDeals(filter?: { status?: string; stageId?: string; suche?: string }): Promise<Deal[]> {
    const response = await api.get('/crm/deals', { params: filter || {} });
    return response.data;
  },

  async getDeal(id: string): Promise<Deal> {
    const response = await api.get(`/crm/deals/${id}`);
    return response.data;
  },

  async createDeal(daten: Partial<Deal>): Promise<Deal> {
    const response = await api.post('/crm/deals', daten);
    return response.data;
  },

  async updateDeal(id: string, daten: Partial<Deal>): Promise<Deal> {
    const response = await api.patch(`/crm/deals/${id}`, daten);
    return response.data;
  },

  async deleteDeal(id: string): Promise<void> {
    await api.delete(`/crm/deals/${id}`);
  },

  async addNote(dealId: string, text: string, kind = 'note'): Promise<DealNote> {
    const response = await api.post(`/crm/deals/${dealId}/notes`, { text, kind });
    return response.data;
  },

  async dealFromActivity(activityId: string, daten?: { title?: string; value?: number }): Promise<{ vorgang: Deal; neu: boolean }> {
    const response = await api.post(`/crm/from-activity/${activityId}`, daten || {});
    return response.data;
  },

  async getSummary(): Promise<PipelineSummary> {
    const response = await api.get('/crm/summary');
    return response.data;
  },

  async getDue(): Promise<Deal[]> {
    const response = await api.get('/crm/due');
    return response.data;
  },

  async getDuplicates(threshold?: number): Promise<{ clusters: DuplicateCluster[]; count: number }> {
    const response = await api.get('/crm/duplicates', { params: threshold ? { threshold } : {} });
    return response.data;
  },

  async merge(primaryId: string, duplicateIds: string[]): Promise<DedupeLead> {
    const response = await api.post('/crm/merge', { primaryId, duplicateIds });
    return response.data;
  },
};

export default crmService;
